import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { PopoverShell } from '@/components/PopoverShell';
import Tooltip from '@/components/Tooltip';
import { GgcButton } from '@/components/ui/ggc/button';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { showSnackbar } from '@/store/slices/snackbarSlice';
import { usePreviewMode } from '@/contexts/PreviewModeContext';
import { useI18nText } from '@/lib/utils/i18nText';
import { formatLocalDateTime, relativeDate, relativeFromNow, updateFreshnessTier, formatDurationSeconds, type UpdateFreshnessTier } from '@/lib/utils/timezone';
import { niceNumber } from '@/lib/utils/misc';
import { updateChannelInstantly } from '@/lib/api/panel.api';
import { pollAsyncRequest } from '@/lib/utils/asyncRequestPoll';
import { INSTANT_UPDATE_PLATFORMS, instantUpdateCooldownGate } from '@/lib/utils/instantUpdateGate';

const FRESHNESS_LEGEND: { tier: UpdateFreshnessTier; label: [string, string] }[] = [
  { tier: 'recent', label: ['Within 3 days', '3일 이내'] },
  { tier: 'stale', label: ['Within 14 days', '14일 이내'] },
  { tier: 'old', label: ['Over 14 days', '14일 초과'] },
];

export interface AffectedDataRow { label: string; value: ReactNode }

/**
 * Builds the "Affected data" rows for the channel-stats crawl (the `stats`
 * field this whole popover is about) from whatever shape of `stats` the
 * caller has on hand — `Influencer` (dashboard) and `PanelUser` (panel) carry
 * slightly different but structurally-compatible shapes, so this takes a
 * loose duck-typed shape rather than importing either page's type.
 *
 * `last5`'s extra fields cover the platforms `GeneralViewershipCell` shows
 * beyond views/likes/comments: `retweets` (Twitter's own "Retweets", but also
 * how Facebook's share count and TikTok/Douyin's "Shares" metric icon are
 * stored — labeled generically as "Shares" here since this summary doesn't
 * split by platform the way that cell's icon does), `replies` (Twitter/
 * Facebook), `saves` (Douyin only).
 *
 * `viewership` covers the platforms whose *headline* metric isn't in
 * `stats.last5` at all: Twitch/CHZZK/SOOP's avg duration/max/avg viewers
 * (`twitchStats`/`chzzkStats`/`afreecaStats`) and Bilibili's plain view count
 * (`bilibiliStats.views`, its `duration`/`maxViewers`/`avgViewers` are absent)
 * — same fields/shapes `GeneralViewershipCell` already reads for those
 * branches. Omit entirely for platforms with neither (TikTok/Douyin/
 * Instagram/Twitter/Facebook).
 *
 * `youtubeBreakdown` is YouTube's own case: confirmed in ggc-be
 * (`updateYoutubeChannels.updater.ts`) that the per-kind Normal/Shorts/Live
 * view counts live in `statsYoutubeBreakdown.last5` (stamped under the
 * *separate* `last5` field, not `stats`) — `stats.last5` itself is a legacy
 * flat field the current updater no longer writes at all, so it's
 * `undefined`/stale on any channel that's only ever been touched by current
 * code (confirmed live: some real channels show it, most don't). Passing the
 * breakdown here — same source `YoutubeViewershipCell` reads — instead of
 * relying on the unreliable flat field.
 */
export function buildStatsAffectedData(
  stats?: {
    allTime?: { subscribers?: number | null };
    last5?: {
      views?: number | null; likes?: number | null; comments?: number | null;
      retweets?: number | null; replies?: number | null; saves?: number | null;
    };
  },
  t: (en: string, ko: string) => string = (en) => en,
  viewership?: {
    views?: number | null;
    duration?: { avg?: number | null } | null;
    maxViewers?: { avg?: number | null } | null;
    avgViewers?: { avg?: number | null } | null;
  } | null,
  youtubeBreakdown?: {
    videos?: { views?: number | null } | null;
    shorts?: { views?: number | null } | null;
    live?: { views?: number | null } | null;
  } | null,
): AffectedDataRow[] {
  const rows: AffectedDataRow[] = [];
  if (stats?.allTime?.subscribers != null) {
    rows.push({ label: t('Subscribers', '구독자'), value: niceNumber(stats.allTime.subscribers) });
  }
  const hasYoutubeBreakdown = youtubeBreakdown && (
    youtubeBreakdown.videos?.views != null
    || youtubeBreakdown.shorts?.views != null
    || youtubeBreakdown.live?.views != null
  );
  if (hasYoutubeBreakdown) {
    if (youtubeBreakdown!.videos?.views != null) {
      rows.push({ label: t('Normal views (last 5)', '일반 조회수 (최근 5)'), value: niceNumber(youtubeBreakdown!.videos!.views) });
    }
    if (youtubeBreakdown!.shorts?.views != null) {
      rows.push({ label: t('Shorts views (last 5)', '쇼츠 조회수 (최근 5)'), value: niceNumber(youtubeBreakdown!.shorts!.views) });
    }
    if (youtubeBreakdown!.live?.views != null) {
      rows.push({ label: t('Live views (last 5)', '라이브 조회수 (최근 5)'), value: niceNumber(youtubeBreakdown!.live!.views) });
    }
  } else if (stats?.last5?.views != null) {
    // Falls back here when there's no breakdown at all (non-YouTube callers
    // never pass `youtubeBreakdown`) — for YouTube specifically, a present
    // but all-empty breakdown object still falls through to this same check.
    rows.push({ label: t('Views (last 5)', '조회수 (최근 5)'), value: niceNumber(stats.last5.views) });
  }
  if (stats?.last5?.likes != null) {
    rows.push({ label: t('Likes (last 5)', '좋아요 (최근 5)'), value: niceNumber(stats.last5.likes) });
  }
  if (stats?.last5?.comments != null) {
    rows.push({ label: t('Comments (last 5)', '댓글 (최근 5)'), value: niceNumber(stats.last5.comments) });
  }
  if (stats?.last5?.retweets != null) {
    rows.push({ label: t('Shares (last 5)', '공유 (최근 5)'), value: niceNumber(stats.last5.retweets) });
  }
  if (stats?.last5?.replies != null) {
    rows.push({ label: t('Replies (last 5)', '답글 (최근 5)'), value: niceNumber(stats.last5.replies) });
  }
  if (stats?.last5?.saves != null) {
    rows.push({ label: t('Saves (last 5)', '저장 (최근 5)'), value: niceNumber(stats.last5.saves) });
  }
  if (viewership?.views != null) {
    rows.push({ label: t('Views', '조회수'), value: niceNumber(viewership.views) });
  }
  if (viewership?.duration?.avg != null) {
    // Stored in ms, like GeneralViewershipCell's twitch/bilibili/chzzk/afreeca branches (`dur / 1000`).
    rows.push({ label: t('Avg. duration', '평균 방송 시간'), value: formatDurationSeconds(viewership.duration.avg / 1000) });
  }
  if (viewership?.avgViewers?.avg != null) {
    rows.push({ label: t('Avg. viewers', '평균 시청자'), value: niceNumber(Math.round(viewership.avgViewers.avg)) });
  }
  if (viewership?.maxViewers?.avg != null) {
    rows.push({ label: t('Max viewers', '최대 시청자'), value: niceNumber(Math.round(viewership.maxViewers.avg)) });
  }
  return rows;
}

type InstantUpdatePhase = 'idle' | 'requesting' | 'polling' | 'done' | 'failed' | 'timedout';

/**
 * "?" help icon next to "Not scheduled" (GGC2-58) — click (not hover) opens a
 * small nested popover explaining why, without overclaiming a single cause:
 * verified in ggc-be that the actual condition differs by platform (e.g.
 * YouTube's updater always sets `${field}NextUpdateAt` once it has run at
 * all — a gap here just means it hasn't run since this field shipped yet;
 * other platforms' shared updater helper only skips it when the user has no
 * channel doc at all). "Channel gone/invalid" is one possible cause, not the
 * only one, so the copy leads with the far more likely one. A click-only
 * PopoverShell (not the hover `Tooltip` used elsewhere in this file) so it
 * doesn't compete with — or get auto-triggered by — this popover's own focus
 * management the way a hover tooltip on the text itself would.
 */
function NotScheduledHelp() {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  return (
    <PopoverShell
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button type="button" className="updated-at-help-icon" aria-label={t('Why is this not scheduled?', '왜 예정이 없나요?')}>
          <i className="fa fa-question-circle" aria-hidden />
        </button>
      }
      side="bottom"
      align="start"
      width={220}
    >
      <div className="updated-at-help-popover">
        {t(
          "No update is scheduled yet — most likely this channel just hasn't been reached by the update cycle yet (rolling out gradually). It can also happen when there's no valid channel to update.",
          '아직 다음 업데이트가 예약되지 않았습니다 — 대부분 이 채널이 아직 업데이트 주기를 한 번도 거치지 않아서입니다(순차 적용 중). 드물게 갱신할 유효한 채널이 없는 경우에도 이렇게 표시될 수 있습니다.',
        )}
      </div>
    </PopoverShell>
  );
}

/**
 * On-demand "refresh now" button for the popover below — same
 * `update-instantly` endpoint + 30-min cooldown gate as `panel/user.tsx`'s
 * own instant-update button and `RefreshChannelStatsPopover`'s bulk refresh
 * (see `lib/utils/instantUpdateGate.ts`), just a single-channel, single-button
 * shape for this smaller popover. Only rendered when the caller passes both
 * `userId` and a `channelType` the BE endpoint accepts.
 */
function InstantUpdateButton({ userId, lastUpdatedIso, onRefreshed }: {
  userId: string;
  lastUpdatedIso?: string | null;
  onRefreshed?: () => void;
}) {
  const { t } = useI18nText();
  const dispatch = useAppDispatch();
  const [phase, setPhase] = useState<InstantUpdatePhase>('idle');
  const cancelRef = useRef<() => void>();
  const flashRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => {
    cancelRef.current?.();
    if (flashRef.current) clearTimeout(flashRef.current);
  }, []);

  const gate = instantUpdateCooldownGate(lastUpdatedIso ?? undefined);
  const busy = phase === 'requesting' || phase === 'polling';
  const disabled = busy || !gate.allowed;

  const flash = (next: 'done' | 'failed' | 'timedout') => {
    setPhase(next);
    if (flashRef.current) clearTimeout(flashRef.current);
    flashRef.current = setTimeout(() => setPhase('idle'), 3000);
  };

  const trigger = async () => {
    if (disabled) return;
    setPhase('requesting');
    let enqueued: { requestId: string; timeoutAt: string };
    try {
      enqueued = await updateChannelInstantly(userId);
    } catch {
      // A real failure (e.g. 400/403) already surfaced via the global
      // error-alert dispatcher — `updateChannelInstantly` uses plain `.post`.
      setPhase('idle');
      return;
    }
    setPhase('polling');
    cancelRef.current?.();
    cancelRef.current = pollAsyncRequest(enqueued.requestId, {
      timeoutAt: enqueued.timeoutAt,
      onTerminal: (result) => {
        if (result.status === 'done') {
          // The BE swallows its own per-channel errors (including a "channel
          // gone" no-op), so `done` confirms the job *ran*, not that it
          // *changed* anything — the real confirmation is whatever this row's
          // "Last updated" shows after `onRefreshed`'s reload.
          flash('done');
          dispatch(showSnackbar({
            text: t('Instant update requested and processed.', '즉시 갱신이 요청 및 처리되었습니다.'),
            kind: 'success',
            duration: 3000,
          }));
          onRefreshed?.();
        } else if (result.status === 'failed') {
          flash('failed');
          dispatch(showSnackbar({ text: result.errorMessage, kind: 'api-error', duration: 4000 }));
        } else if (result.status === 'timedout' || result.status === 'client-timedout') {
          flash('timedout');
          dispatch(showSnackbar({
            text: t('Instant update timed out — try again later.', '즉시 갱신이 시간 초과되었습니다 — 잠시 후 다시 시도하세요.'),
            kind: 'api-error',
            duration: 4000,
          }));
        } else {
          flash('failed');
          dispatch(showSnackbar({
            text: t('Lost track of the update request (it may have expired) — try again.', '갱신 요청을 더 이상 추적할 수 없습니다(만료되었을 수 있음) — 다시 시도하세요.'),
            kind: 'api-error',
            duration: 4000,
          }));
        }
      },
    });
  };

  const cooldownMinutesLeft = gate.elapsedMs != null
    ? Math.ceil((30 * 60_000 - gate.elapsedMs) / 60_000)
    : 0;

  return (
    <Tooltip
      essential
      // This button sits at the bottom of a small popover — Radix focuses it
      // automatically when the popover opens (it's the first/only focusable
      // element), which triggers this Tooltip via focus as well as hover. The
      // default "top" side then overlaps the popover's own content above it,
      // so pin it to "bottom" instead (there's open space below, outside the
      // popover) regardless of which trigger (focus or hover) opened it.
      side="bottom"
      content={
      phase === 'done' ? t('Done', '완료됨')
        : phase === 'failed' ? t('Failed — see the error toast', '실패 — 오류 토스트 참고')
          : phase === 'timedout' ? t('Timed out', '시간 초과됨')
            : busy ? t('Requesting…', '요청 중…')
              : !gate.allowed
                ? t(
                  `Refreshed too recently — this shares a company-wide vendor quota, so it's rate-limited to once per 30 minutes per channel. Try again in ${cooldownMinutesLeft}m.`,
                  `너무 최근에 갱신했습니다 — 회사 전체 공용 벤더 쿼터를 사용하기 때문에 채널당 30분에 한 번으로 제한됩니다. ${cooldownMinutesLeft}분 후 다시 시도하세요.`,
                )
                : t('Refresh this channel now (shares a company-wide vendor quota — use only when necessary)', '이 채널을 지금 갱신합니다 (회사 전체 공용 벤더 쿼터를 사용 — 꼭 필요할 때만 사용하세요)')
    }>
      <GgcButton
        type="button"
        variant={phase === 'done' ? 'success' : phase === 'failed' || phase === 'timedout' ? 'danger' : 'primary'}
        size="sm"
        className="w-full"
        disabled={disabled}
        onClick={() => void trigger()}
      >
        {busy ? (
          <i className="fa fa-circle-o-notch fa-spin" aria-hidden />
        ) : phase === 'done' ? (
          <i className="fa fa-check" aria-hidden />
        ) : phase === 'failed' || phase === 'timedout' ? (
          <i className="fa fa-exclamation-triangle" aria-hidden />
        ) : (
          <i className="fa fa-bolt" aria-hidden />
        )}
        {t('Refresh now', '지금 갱신')}
      </GgcButton>
    </Tooltip>
  );
}

/**
 * "Updated at" freshness indicator — a small gray underlined-link chip showing
 * the relative age ("Updated At 2d ago"); clicking it opens a popover with the
 * absolute date/time. Shared by the dashboard freshness lines (ColumnUpdatedAt)
 * and the panel Channels-tab "Updated At" column so both read identically.
 * Returns null when there is no timestamp. Not gated — callers decide visibility.
 *
 * Popover-ized (rather than showing the absolute date inline below, as before)
 * so the next scheduled update (GGC2-58) has somewhere to go as a second
 * labeled row without needing another redesign.
 */
export function UpdatedAtLines({
  iso, nextUpdateIso, userId, channelType, onRefreshed,
  channelThumbnail, channelName, affectedData,
}: {
  iso?: string | null;
  /** GGC2-58 `${field}NextUpdateAt` — omit/`null` when not scheduled. */
  nextUpdateIso?: string | null;
  /** Together with `channelType`, opts into an inline "Refresh now" (on-demand
   *  `update-instantly`) button — omit either to hide it. */
  userId?: string;
  channelType?: string;
  /** Called after a successful/terminal instant-update run, so the caller can
   *  silently reload this row's data (the popover itself doesn't own that state). */
  onRefreshed?: () => void;
  /** Identity header (thumbnail + platform icon + name + close ✕) — same treatment
   *  as every other channel-row popover (EmailCell, EstPriceCell's Channel Rates,
   *  CampaignHistoryPopover, TagEditPopover). Omit either to skip the header —
   *  useful since this popover can float far enough from its row to lose context
   *  about which channel it belongs to. */
  channelThumbnail?: string;
  channelName?: string;
  /** "Affected data" — the current values of whatever fields this stats crawl
   *  keeps fresh (subscribers, last-5 views/likes/comments, …), so an admin can
   *  see what "instant update" would actually touch. Build with
   *  `buildStatsAffectedData`. Omit/empty to skip the section. */
  affectedData?: AffectedDataRow[];
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [affectedExpanded, setAffectedExpanded] = useState(true);
  if (!iso) return null;
  const tier = updateFreshnessTier(iso);
  const canInstantUpdate = !!userId && !!channelType && INSTANT_UPDATE_PLATFORMS.has(channelType);
  const hasHeader = !!channelName;

  // Elapsed share of the last-updated → next-update window, visualized as a
  // thin bar under the "Next update" row — a glance at how close the next
  // scheduled crawl is, not just the two absolute dates. `null` when there's
  // nothing to compute against (unscheduled, or a malformed/inverted pair).
  const lastMs = new Date(iso).getTime();
  const nextMs = nextUpdateIso ? new Date(nextUpdateIso).getTime() : NaN;
  const hasValidWindow = nextUpdateIso && !Number.isNaN(lastMs) && !Number.isNaN(nextMs) && nextMs > lastMs;
  const overdue = hasValidWindow && Date.now() >= nextMs;
  const progressPct = hasValidWindow
    ? Math.max(0, Math.min(100, ((Date.now() - lastMs) / (nextMs - lastMs)) * 100))
    : null;
  return (
    <PopoverShell
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button type="button" className="updated-at-trigger">
          <span className={`updated-at-trigger__dot updated-at-trigger__dot--${tier}`} aria-hidden />
          {t('Updated At', '갱신일')}{' '}
          <span className={`updated-at-value updated-at-value--${tier}`}>{relativeDate(iso)}</span>
        </button>
      }
      side="bottom"
      align="start"
      width={270}
    >
      {hasHeader && (
        <div className="est-price-popover-header">
          <ChannelThumbnail src={channelThumbnail} />
          <ChannelTypeIcon type={channelType ?? 'youtube'} size={14} asBadge />
          <span className="est-price-popover-name">{channelName}</span>
          <button
            type="button"
            className="est-price-popover-close"
            onClick={() => setOpen(false)}
            aria-label={t('Close', '닫기')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="updated-at-popover-row">
        <span className="updated-at-popover-label">{t('Last updated', '최신 업데이트 일자')}</span>
        <span className="updated-at-popover-value">
          <span className={`updated-at-trigger__dot updated-at-trigger__dot--${tier}`} aria-hidden />
          <span>{formatLocalDateTime(iso)}</span>
          <span className="updated-at-popover-value__relative">({relativeFromNow(iso)})</span>
        </span>
      </div>
      <div className="updated-at-popover-row">
        <span className="updated-at-popover-label">{t('Next update', '다음 갱신 예정')}</span>
        {nextUpdateIso ? (
          <span className="updated-at-popover-value">
            <span>{formatLocalDateTime(nextUpdateIso)}</span>
            <span className="updated-at-popover-value__relative">({relativeFromNow(nextUpdateIso)})</span>
          </span>
        ) : (
          <span className="updated-at-popover-value">
            <span className="updated-at-popover-value--muted">{t('Not scheduled', '예정 없음')}</span>
            <NotScheduledHelp />
          </span>
        )}
      </div>
      {progressPct != null && (
        <div className="updated-at-progress" title={overdue ? t('Overdue', '예정 시각 지남') : undefined}>
          <div
            className={`updated-at-progress__fill${overdue ? ' updated-at-progress__fill--overdue' : ''}`}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      )}
      {/* Color-coding legend — the relative-time label above (and every "Updated
          At" chip in the table) is tinted per this same tiering, so the meaning
          of the color is discoverable from any one instance's popover. */}
      <div className="updated-at-popover-legend">
        {FRESHNESS_LEGEND.map(({ tier: legendTier, label }) => (
          <span key={legendTier} className="updated-at-popover-legend__row">
            <span className={`updated-at-popover-legend__dot updated-at-popover-legend__dot--${legendTier}`} />
            {t(...label)}
          </span>
        ))}
      </div>
      {affectedData && affectedData.length > 0 && (
        <div className="updated-at-popover-affected">
          <button
            type="button"
            className="updated-at-popover-affected__title updated-at-popover-affected__title--toggle"
            onClick={() => setAffectedExpanded((v) => !v)}
            aria-expanded={affectedExpanded}
          >
            {t('Affected data', '영향받는 데이터')}
            <i className={`fa fa-angle-${affectedExpanded ? 'up' : 'down'}`} aria-hidden />
          </button>
          {affectedExpanded && affectedData.map((row) => (
            <div key={row.label} className="updated-at-popover-row updated-at-popover-row--tight">
              <span className="updated-at-popover-label">
                <span className="updated-at-popover-affected__bullet" aria-hidden />
                {row.label}
              </span>
              <span className="updated-at-popover-value">{row.value}</span>
            </div>
          ))}
        </div>
      )}
      {canInstantUpdate && (
        <div className="updated-at-popover-actions">
          <InstantUpdateButton userId={userId!} lastUpdatedIso={iso} onRefreshed={onRefreshed} />
        </div>
      )}
    </PopoverShell>
  );
}

/**
 * Muted "last updated" freshness line pinned at the bottom of a data column's cell
 * so an admin can gauge how fresh THAT column's data is (channel stats crawl).
 *
 * Admin-only: hidden in advertiser-preview and guest-token sessions (mirrors the
 * `role === 'admin' && !isPreview` gate used by ChannelNameQuickButtons / V1CompareButton),
 * so this internal freshness metadata never leaks into an advertiser-facing view.
 */
export function ColumnUpdatedAt({
  iso, nextUpdateIso, userId, channelType, onRefreshed, channelThumbnail, channelName, affectedData,
}: {
  iso?: string | null;
  nextUpdateIso?: string | null;
  userId?: string;
  channelType?: string;
  onRefreshed?: () => void;
  channelThumbnail?: string;
  channelName?: string;
  affectedData?: AffectedDataRow[];
}) {
  const role = useAppSelector((s) => s.envs.currentUser?.role);
  const isPreview = usePreviewMode();
  if (role !== 'admin' || isPreview) return null;
  if (!iso) return null;
  return (
    <div className="column-updated-at">
      <UpdatedAtLines
        iso={iso}
        nextUpdateIso={nextUpdateIso}
        userId={userId}
        channelType={channelType}
        onRefreshed={onRefreshed}
        channelThumbnail={channelThumbnail}
        channelName={channelName}
        affectedData={affectedData}
      />
    </div>
  );
}
